import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { from, Subscription, switchMap } from 'rxjs';
import { DiscoverySphereFirestoreService } from './discovery-sphere-firestore.service';
import {
  byTitle,
  cardsFor,
  identityHash,
  latestSphere,
  reachingCount,
  selectLanes,
  utcDate,
  type DiscoverySphere,
  type LaneSummary,
} from './discovery-sphere.model';

/** The one browser-storage key of this feature: the viewer's last explicit lane choice. */
export const DISCOVERY_SPHERE_LANES_KEY = 'agentic-console.discovery-sphere.lanes';

interface DiscoverySphereState {
  spheres: DiscoverySphere[];
  loading: boolean;
  error: string | null;
  /** No lane selected means all lanes. */
  selectedLanes: string[];
  /** Why those lanes are selected, for display. */
  selectionReason: string;
}

const initialState: DiscoverySphereState = {
  spheres: [],
  loading: false,
  error: null,
  selectedLanes: [],
  selectionReason: '',
};

/** Browser storage can be absent, blocked or full; the view then works without remembering. */
function readChoice(): string[] | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(DISCOVERY_SPHERE_LANES_KEY) ?? 'null');
    return Array.isArray(value) && value.every((lane) => typeof lane === 'string') ? value : null;
  } catch {
    return null;
  }
}

function writeChoice(lanes: readonly string[]): void {
  try {
    localStorage.setItem(DISCOVERY_SPHERE_LANES_KEY, JSON.stringify(lanes));
  } catch {
    // The choice still applies to this visit.
  }
}

export const DiscoverySphereStore = signalStore(
  withState(initialState),
  withComputed((store) => {
    const presentSpheres = computed(() => store.spheres().filter((sphere) => sphere.present));
    const latest = computed(() => latestSphere(presentSpheres()));
    return {
      presentSpheres,
      latestSphere: latest,
      waiting: computed(() =>
        presentSpheres().reduce((sum, sphere) => sum + sphere.waiting, 0),
      ),
      /** Lane totals and sphere counts are derived directly from the present sphere documents. */
      laneSummaries: computed<LaneSummary[]>(() => {
        const current = latest();
        if (!current) return [];
        const present = presentSpheres();
        return current.directoryLanes.map((lane) => ({
          lane,
          spheres: present.filter((sphere) => reachingCount(sphere, [lane]) > 0).length,
          decisions: present.reduce(
            (sum, sphere) =>
              sum + (sphere.waitingByLane.find((item) => item.lane === lane)?.count ?? 0),
            0,
          ),
        }));
      }),
      allLanes: computed<LaneSummary>(() => {
        const present = presentSpheres();
        return {
          lane: '',
          spheres: present.filter((sphere) => sphere.waiting > 0).length,
          decisions: present.reduce((sum, sphere) => sum + sphere.waiting, 0),
        };
      }),
      cards: computed(() => cardsFor(presentSpheres(), store.selectedLanes())),
      /** Spheres with a waiting decision that reaches no lane. */
      withoutLane: computed(() =>
        presentSpheres()
          .filter((sphere) => sphere.waitingWithoutLane > 0)
          .sort(byTitle),
      ),
      /** Spheres whose pitch holds no decision table that could be read. */
      tableNotRead: computed(() =>
        presentSpheres()
          .filter((sphere) => !sphere.decisionTableRead)
          .sort(byTitle),
      ),
    };
  }),
  withMethods((store, firestore = inject(DiscoverySphereFirestoreService)) => {
    let subscription: Subscription | null = null;
    let choice: string[] | null = null;
    let viewerHash = '';
    /** Derived again on every snapshot and every choice, so the rule lives in one place. */
    const selection = (spheres: readonly DiscoverySphere[]) => {
      const current = latestSphere(spheres);
      const selected = current
        ? selectLanes(current, choice, viewerHash, utcDate(new Date()))
        : { lanes: [], reason: '' };
      return { selectedLanes: selected.lanes, selectionReason: selected.reason };
    };
    const choose = (lanes: string[]): void => {
      choice = lanes;
      writeChoice(lanes);
      patchState(store, selection(store.spheres()));
    };
    return {
      /** `email` is the signed-in viewer's own address. It is hashed here and sent nowhere. */
      load(email: string | null): void {
        subscription?.unsubscribe();
        patchState(store, { loading: true, error: null });
        choice = readChoice() ?? choice;
        subscription = from(identityHash(email).catch(() => ''))
          .pipe(
            switchMap((hash) => {
              viewerHash = hash;
              return firestore.watchSpheres();
            }),
          )
          .subscribe({
            // A lane that leaves the directory leaves the selection; an explicit choice stays first.
            next: (spheres) =>
              patchState(store, { spheres, loading: false, ...selection(spheres) }),
            error: () =>
              patchState(store, {
                loading: false,
                error: 'Unable to load discovery spheres. Check your access and try again.',
              }),
          });
      },
      /** Lanes are multi-select. Every explicit change is remembered in this browser. */
      toggleLane(lane: string): void {
        const lanes = store.latestSphere()?.directoryLanes ?? [];
        if (!lanes.includes(lane)) return;
        const selected = store.selectedLanes();
        const next = selected.includes(lane)
          ? selected.filter((item) => item !== lane)
          : [...selected, lane];
        choose(lanes.filter((item) => next.includes(item)));
      },
      selectAllLanes(): void {
        choose([]);
      },
      destroy(): void {
        subscription?.unsubscribe();
      },
    };
  }),
  withHooks({ onDestroy: (store) => store.destroy() }),
);
